"use client";

import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import { TrackedLink } from "@/components/analytics/TrackedLink";
import { GOOGLE_REVIEW_PROOF_LABEL, GOOGLE_REVIEW_RATING } from "@/lib/reviewProof";
import { recentReviewProof } from "@/lib/recentReviewProof";

type ReviewCard = {
  name: string;
  focus: string;
  quote: string;
  portrait: string;
  googleImage: string;
};

type SupportingReviewCard = Omit<ReviewCard, "portrait" | "googleImage"> & {
  portrait?: string;
  googleImage?: string;
  sourceUrl?: string;
};

const proofVideoPoster = "/images/testimonials/youtube/ali-proof-hero-cover.png";
const proofVideoEmbedUrl =
  "https://www.youtube-nocookie.com/embed/QCvj6q45Kj4?autoplay=1&rel=0&modestbranding=1&playsinline=1";

const reviewCards: ReviewCard[] = [
  {
    name: "Oliver Milne",
    focus: "Student review",
    quote: "Gave honest feedback and identified areas we could work on together.",
    portrait: "/images/reviews/oliver-milne.jpg",
    googleImage: "/images/reviews/google/oliver-milne-review.png",
  },
  {
    name: "Shimon Atunde",
    focus: "Ongoing coaching",
    quote: "Liuba is a talented teacher. Her approach, motivation, enthusiasm and encouragement is something I value greatly in our sessions.",
    portrait: "/images/reviews/shimon-atunde.jpg",
    googleImage: "/images/reviews/google/shimon-atunde-review.png",
  },
  {
    name: "Andrew Farrer",
    focus: "Singing fundamentals",
    quote: "With repetition of real fundamentals and a lot of patience, I feel I'm building a strong basis.",
    portrait: "/images/reviews/andrew-farrer.jpg",
    googleImage: "/images/reviews/google/andrew-farrer-review.png",
  },
  {
    name: "Hannah Smith",
    focus: "Confidence and technique",
    quote: "Liuba instantly made me feel more at ease, and she was very understanding of my challenges.",
    portrait: "/images/reviews/hannah-smith-v2.jpg",
    googleImage: "/images/reviews/google/hannah-smith-review.png",
  },
  {
    name: "Bharat Bhardwaj",
    focus: "Voice development",
    quote: "Liuba is a very serious and thorough teacher.",
    portrait: "/images/reviews/bharat-bhardwaj.jpg",
    googleImage: "/images/reviews/google/bharat-bhardwaj-review.png",
  },
];

const featuredReviewCard = reviewCards[0];
const sidebarReviewCard = reviewCards.find((review) => review.name === "Bharat Bhardwaj") ?? reviewCards[reviewCards.length - 1];
const supportingReviewCards: SupportingReviewCard[] = [
  ...recentReviewProof.map((review) => ({
    name: review.author,
    focus: "Student review",
    quote: review.quote,
    sourceUrl: review.sourceUrl,
  })),
  ...reviewCards.filter(
    (review) => review.name !== featuredReviewCard.name && review.name !== sidebarReviewCard.name,
  ),
];

function reviewInitials(name: string) {
  return name.split(" ").map((part) => part[0]).join("").slice(0, 2).toUpperCase();
}

function GoogleGIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <path fill="#4285F4" d="M23.5 12.27c0-.79-.07-1.55-.2-2.27H12v4.29h6.47a5.53 5.53 0 0 1-2.4 3.63v3.01h3.88c2.27-2.09 3.55-5.17 3.55-8.66z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.07 7.93-2.91l-3.88-3.01c-1.08.72-2.45 1.14-4.05 1.14-3.11 0-5.75-2.1-6.69-4.93H1.3v3.09A12 12 0 0 0 12 24z" />
      <path fill="#FBBC05" d="M5.31 14.29A7.2 7.2 0 0 1 4.94 12c0-.79.13-1.56.37-2.29V6.62H1.3A12 12 0 0 0 0 12c0 1.94.46 3.77 1.3 5.38l4.01-3.09z" />
      <path fill="#EA4335" d="M12 4.78c1.76 0 3.34.61 4.59 1.8l3.43-3.43C17.94 1.22 15.23 0 12 0A12 12 0 0 0 1.3 6.62l4.01 3.09C6.25 6.88 8.89 4.78 12 4.78z" />
    </svg>
  );
}

function Stars() {
  return (
    <span role="img" aria-label={`${GOOGLE_REVIEW_RATING} out of 5 stars`} className="inline-flex gap-0.5 text-[#d3a33b]">
      {Array.from({ length: 5 }).map((_, index) => (
        <svg key={index} viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-current">
          <path d="M12 2.4 14.9 8.3l6.5.9-4.7 4.6 1.1 6.5L12 17.2l-5.8 3.1 1.1-6.5-4.7-4.6 6.5-.9L12 2.4Z" />
        </svg>
      ))}
    </span>
  );
}

function ProofVideoCard() {
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <div className="space-y-3">
      <div
        className="group relative block aspect-[16/10] overflow-hidden rounded-[16px] border border-[#e6dac8] bg-[#123b34] shadow-[0_16px_42px_rgba(49,39,24,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c99422]/50 focus-visible:ring-offset-2"
      >
        {isPlaying ? (
          <iframe
            src={proofVideoEmbedUrl}
            title="Ali Abdaal public proof video"
            className="absolute inset-0 h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            data-amp-href={proofVideoEmbedUrl}
            onClick={() => setIsPlaying(true)}
            aria-label="Play Ali Abdaal public proof video"
            className="absolute inset-0 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c99422]/50 focus-visible:ring-offset-2"
          >
            <Image
              src={proofVideoPoster}
              alt="Ali Abdaal public proof video still"
              fill
              sizes="(min-width: 1024px) 22rem, 90vw"
              className="object-cover transition duration-500 group-hover:scale-[1.03]"
            />
            <span className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.06),rgba(0,0,0,0.56))]" aria-hidden="true" />
            <span className="absolute left-1/2 top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/92 text-[#d3a33b] shadow-[0_12px_32px_rgba(0,0,0,0.25)] transition group-hover:scale-105" aria-hidden="true">
              <svg viewBox="0 0 24 24" className="ml-1 h-6 w-6 fill-current">
                <path d="M8 5.5v13l10-6.5-10-6.5Z" />
              </svg>
            </span>
            <span className="absolute bottom-4 left-4 right-4">
              <span className="block text-[0.68rem] font-black uppercase tracking-[0.28em] text-[#f2cf6b]">Student proof</span>
              <span className="mt-1.5 block font-serif text-[1.35rem] font-semibold leading-tight text-white">
                Real voice change, real trust.
              </span>
            </span>
          </button>
        )}
      </div>

      <div className="rounded-[14px] border border-[#e6dac8] bg-[linear-gradient(135deg,#0b4b42,#062f29)] p-3.5 text-white shadow-[0_12px_32px_rgba(6,47,41,0.14)]">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#d3a33b]/35 bg-[#d3a33b]/15 text-[#f2cf6b]">
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-current">
              <path d="M8 5.5v13l10-6.5-10-6.5Z" />
            </svg>
          </span>
          <div>
            <p className="font-bold text-white">Video proof from Ali Abdaal</p>
            <p className="mt-1 text-sm leading-5 text-white/72">Public student proof, embedded from the original video.</p>
          </div>
        </div>
      </div>

      <article className="overflow-hidden rounded-[14px] border border-[#e6dac8] bg-white/86 shadow-[0_10px_28px_rgba(49,39,24,0.045)]">
        <div className="relative h-14 border-b border-[#efe5d4] bg-[#fffaf2]">
          <Image
            src={sidebarReviewCard.googleImage}
            alt={`${sidebarReviewCard.name} public Google review screenshot`}
            fill
            sizes="(min-width: 1024px) 20rem, 90vw"
            className="object-cover object-top opacity-60"
          />
          <span className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-[0_10px_30px_rgba(49,39,24,0.08)]">
            <GoogleGIcon className="h-4 w-4" />
          </span>
        </div>
        <div className="p-3.5">
          <div className="flex items-center gap-3">
            <span className="relative h-10 w-10 overflow-hidden rounded-full border border-[#e6dac8] bg-[#fffaf2]">
              <Image src={sidebarReviewCard.portrait} alt={`${sidebarReviewCard.name} portrait`} fill sizes="2.5rem" className="object-cover" />
            </span>
            <div>
              <h3 className="text-base font-bold leading-tight text-[#123b34]">{sidebarReviewCard.name}</h3>
              <p className="text-sm font-semibold text-[#a06c15]">{sidebarReviewCard.focus}</p>
            </div>
          </div>
          <div className="mt-3">
            <Stars />
          </div>
          <p className="mt-2 text-sm leading-5 text-[#4f5d58]">&ldquo;{sidebarReviewCard.quote}&rdquo;</p>
          <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#eef7f2] px-3 py-1.5 text-xs font-bold text-[#0b4b42]">
            <GoogleGIcon className="h-4 w-4" />
            Public Google review
          </span>
        </div>
      </article>
    </div>
  );
}

function CompactProofVideoCard() {
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <div className="flex min-w-0 flex-col gap-3 lg:h-full" data-proof-column="ali-and-bharat">
      <div className="group relative aspect-video overflow-hidden rounded-[16px] border border-[#d9c49e] bg-[#123b34] shadow-[0_18px_44px_rgba(49,39,24,0.12)]">
        {isPlaying ? (
          <iframe
            src={proofVideoEmbedUrl}
            title="Ali Abdaal public proof video"
            className="absolute inset-0 h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            data-amp-href={proofVideoEmbedUrl}
            onClick={() => setIsPlaying(true)}
            aria-label="Play Ali Abdaal public proof video"
            className="absolute inset-0 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#f2cf6b]"
          >
            <Image
              src={proofVideoPoster}
              alt="Ali Abdaal public proof video still"
              fill
              sizes="(min-width: 1024px) 40vw, 94vw"
              className="object-cover transition duration-500 group-hover:scale-[1.025]"
            />
            <span className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.02)_32%,rgba(0,0,0,0.82)_100%)]" aria-hidden="true" />
            <span className="absolute inset-x-0 bottom-0 p-3 sm:p-5">
              <span className="hidden text-[0.65rem] font-black uppercase tracking-[0.3em] text-[#f2cf6b] sm:block">Student proof</span>
              <span className="block font-serif text-[1.05rem] font-semibold leading-none text-white sm:mt-1 sm:text-[1.75rem]">
                Real voice change, real trust.
              </span>
              <span className="mt-2 flex flex-col gap-0.5 border-t border-white/22 pt-2 text-[0.72rem] leading-4 sm:mt-3 sm:flex-row sm:items-center sm:gap-3 sm:pt-3 sm:text-sm">
                <span className="font-black text-white">Video proof from Ali Abdaal</span>
                <span className="hidden h-4 w-px bg-white/32 sm:block" aria-hidden="true" />
                <span className="text-white/72">Public student proof, embedded from the original video.</span>
              </span>
            </span>
          </button>
        )}
      </div>

      <article
        className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] grid-rows-[2rem_minmax(0,1fr)] overflow-hidden rounded-[14px] border border-[#e0cfb4] bg-white shadow-[0_10px_28px_rgba(49,39,24,0.05)] lg:flex-1"
        data-proof-card="bharat"
      >
        <div className="row-span-2 flex items-center gap-2 p-2.5 sm:gap-3 sm:p-3">
          <span className="relative h-8 w-8 shrink-0 overflow-hidden rounded-full border border-[#e6dac8] bg-[#fffaf2] sm:h-10 sm:w-10">
            <Image src={sidebarReviewCard.portrait} alt={`${sidebarReviewCard.name} portrait`} fill sizes="2.5rem" className="object-cover" />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-black leading-tight text-[#123b34]">{sidebarReviewCard.name}</h3>
            <p className="text-xs font-bold text-[#a06c15]">{sidebarReviewCard.focus}</p>
            <div className="mt-1.5"><Stars /></div>
          </div>
        </div>
        <div className="relative h-8 border-b border-l border-[#eee2cf] bg-[#fffaf2]" data-proof-google-strip="bharat">
          <Image src={sidebarReviewCard.googleImage} alt={`${sidebarReviewCard.name} public Google review screenshot`} fill sizes="(min-width: 1024px) 16rem, 55vw" className="object-cover object-top opacity-62" />
          <span className="absolute right-2 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-sm">
            <GoogleGIcon className="h-3.5 w-3.5" />
          </span>
        </div>
        <p className="border-l border-[#eee2cf] px-2.5 py-2 text-[0.72rem] leading-4 text-[#4f5d58] sm:px-3 sm:py-2.5 sm:text-[0.8rem] sm:leading-5">
          &ldquo;{sidebarReviewCard.quote}&rdquo;
        </p>
      </article>
    </div>
  );
}

function ReviewCard({ review }: { review: SupportingReviewCard }) {
  return (
    <article className="flex min-h-[14rem] flex-col overflow-hidden rounded-[14px] border border-[#e6dac8] bg-white shadow-[0_10px_28px_rgba(49,39,24,0.045)]">
      <div className="relative h-14 border-b border-[#efe5d4] bg-[#fffaf2]">
        {review.googleImage ? <Image src={review.googleImage} alt={`${review.name} public Google review screenshot`} fill sizes="(min-width: 1024px) 22rem, 90vw" className="object-cover object-top opacity-55" /> : <p className="flex h-full items-center px-3 pr-14 text-xs font-semibold text-[#68736e]">Public Google review</p>}
        <span className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-[0_10px_30px_rgba(49,39,24,0.08)]">
          <GoogleGIcon className="h-4 w-4" />
        </span>
      </div>
      <div className="flex flex-1 flex-col p-3.5">
        <div className="flex items-center gap-3">
          <span className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#e6dac8] bg-[#fffaf2] text-xs font-bold text-[#123b34]">
            {review.portrait ? <Image src={review.portrait} alt={`${review.name} portrait`} fill sizes="2.5rem" className="object-cover" /> : <span role="img" aria-label={`${review.name} initials`}>{reviewInitials(review.name)}</span>}
          </span>
          <div>
            <h3 className="text-base font-bold leading-tight text-[#123b34]">{review.name}</h3>
            <p className="text-sm font-semibold text-[#a06c15]">{review.focus}</p>
          </div>
        </div>
        <div className="mt-3">
          <Stars />
        </div>
        <p className="mt-2 flex-1 text-sm leading-5 text-[#4f5d58]">&ldquo;{review.quote}&rdquo;</p>
        <div className="mt-4">
          {review.sourceUrl ? <a href={review.sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-2 rounded-full bg-[#eef7f2] px-3 py-1.5 text-xs font-bold text-[#0b4b42] underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0b4b42]">
            <GoogleGIcon className="h-4 w-4" />
            View Google reviews
          </a> : <span className="inline-flex items-center gap-2 rounded-full bg-[#eef7f2] px-3 py-1.5 text-xs font-bold text-[#0b4b42]">
            <GoogleGIcon className="h-4 w-4" />
            Public Google review
          </span>}
        </div>
      </div>
    </article>
  );
}

function CompactReviewCard({ review }: { review: SupportingReviewCard }) {
  return (
    <article className="flex min-h-[10.5rem] flex-col overflow-hidden rounded-[13px] border border-[#e0cfb4] bg-white shadow-[0_9px_24px_rgba(49,39,24,0.045)]">
      <div className="relative h-8 shrink-0 border-b border-[#eee2cf] bg-[#fffaf2]">
        {review.googleImage ? <Image src={review.googleImage} alt={`${review.name} public Google review screenshot`} fill sizes="(min-width: 1280px) 15rem, 80vw" className="object-cover object-top opacity-62" /> : <p className="flex h-full items-center px-2.5 pr-10 text-[0.62rem] font-semibold text-[#68736e]">Public Google review</p>}
        <span className="absolute right-2 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-sm">
          <GoogleGIcon className="h-3.5 w-3.5" />
        </span>
      </div>
      <div className="flex flex-1 flex-col p-2.5">
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#e6dac8] bg-[#fffaf2] text-xs font-bold text-[#123b34]">
            {review.portrait ? <Image src={review.portrait} alt={`${review.name} portrait`} fill sizes="2rem" className="object-cover" /> : <span role="img" aria-label={`${review.name} initials`}>{reviewInitials(review.name)}</span>}
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-black leading-tight text-[#123b34]">{review.name}</h3>
            <p className="text-xs font-bold leading-4 text-[#a06c15]">{review.focus}</p>
          </div>
        </div>
        <div className="mt-1.5"><Stars /></div>
        <p className="mt-1.5 flex-1 text-[0.74rem] leading-[1.05rem] text-[#4f5d58]">&ldquo;{review.quote}&rdquo;</p>
        {review.sourceUrl ? <a href={review.sourceUrl} target="_blank" rel="noopener noreferrer" className="mt-1.5 inline-flex min-h-11 items-center gap-1.5 self-start text-[0.68rem] font-bold text-[#0b4b42] underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0b4b42]">
          <GoogleGIcon className="h-3.5 w-3.5" />
          View Google reviews
        </a> : <span className="mt-1.5 inline-flex items-center gap-1.5 self-start text-[0.62rem] font-black text-[#0b4b42]">
          <GoogleGIcon className="h-3.5 w-3.5" />
          Public Google review
        </span>}
      </div>
    </article>
  );
}

function FeaturedReviewCard({ review }: { review: ReviewCard }) {
  return (
    <article className="overflow-hidden rounded-[16px] border border-[#dfceb2] bg-[linear-gradient(135deg,#ffffff,#fff8eb)] shadow-[0_14px_36px_rgba(49,39,24,0.065)]">
      <div className="grid gap-0 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="relative min-h-[10rem] border-b border-[#eadcc4] bg-[#fffaf2] lg:border-b-0 lg:border-r">
          <Image
            src={review.googleImage}
            alt={`${review.name} featured public Google review screenshot`}
            fill
            sizes="(min-width: 1024px) 20rem, 90vw"
            className="object-cover object-top opacity-70"
          />
          <span className="absolute right-4 top-4 inline-flex items-center gap-2 rounded-full bg-white/92 px-3 py-1.5 text-xs font-black text-[#123b34] shadow-[0_12px_30px_rgba(49,39,24,0.1)]">
            <GoogleGIcon className="h-4 w-4" />
            Featured review
          </span>
        </div>
        <div className="p-4 sm:p-5">
          <div className="flex items-center gap-3">
            <span className="relative h-12 w-12 overflow-hidden rounded-full border border-[#dfceb2] bg-[#fffaf2]">
              <Image src={review.portrait} alt={`${review.name} portrait`} fill sizes="3rem" className="object-cover" />
            </span>
            <div>
              <h3 className="text-lg font-black leading-tight text-[#123b34]">{review.name}</h3>
              <p className="text-sm font-bold text-[#a06c15]">{review.focus}</p>
            </div>
          </div>
          <div className="mt-4">
            <Stars />
          </div>
          <p className="mt-3 font-serif text-[1.28rem] font-semibold leading-tight text-[#123b34]">
            &ldquo;{review.quote}&rdquo;
          </p>
          <p className="mt-3 text-sm leading-6 text-[#5f6b66]">
            Honest feedback, patient coaching and a voice that feels easier to understand.
          </p>
        </div>
      </div>
    </article>
  );
}

function CompactFeaturedReviewCard({ review }: { review: ReviewCard }) {
  return (
    <article className="overflow-hidden rounded-[14px] border border-[#dfceb2] bg-[linear-gradient(135deg,#ffffff,#fff9ef)] shadow-[0_12px_30px_rgba(49,39,24,0.055)]">
      <div className="grid sm:grid-cols-[0.78fr_1.22fr]">
        <div className="relative min-h-[6.75rem] border-b border-[#eadcc4] bg-[#fffaf2] sm:border-b-0 sm:border-r">
          <Image src={review.googleImage} alt={`${review.name} featured public Google review screenshot`} fill sizes="(min-width: 1024px) 24vw, 90vw" className="object-cover object-top opacity-72" />
          <span className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/94 px-2.5 py-1 text-[0.68rem] font-black text-[#123b34] shadow-sm">
            <GoogleGIcon className="h-3.5 w-3.5" />
            Featured review
          </span>
        </div>
        <div className="p-3">
          <div className="flex items-center gap-3">
            <span className="relative h-10 w-10 shrink-0 overflow-hidden rounded-full border border-[#dfceb2] bg-[#fffaf2]">
              <Image src={review.portrait} alt={`${review.name} portrait`} fill sizes="2.5rem" className="object-cover" />
            </span>
            <div className="min-w-0">
              <h3 className="text-base font-black leading-tight text-[#123b34]">{review.name}</h3>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                <p className="text-xs font-bold text-[#a06c15]">{review.focus}</p>
                <Stars />
              </div>
            </div>
          </div>
          <p className="mt-2.5 font-serif text-[1.08rem] font-semibold leading-[1.16] text-[#123b34] sm:text-[1.18rem]">
            &ldquo;{review.quote}&rdquo;
          </p>
          <p className="mt-1.5 text-[0.75rem] leading-4 text-[#5f6b66]">Honest feedback, patient coaching and a voice that feels easier to understand.</p>
        </div>
      </div>
    </article>
  );
}

function ReviewCardCarousel({ compact = false }: { compact?: boolean }) {
  const [index, setIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);

  useEffect(() => {
    const updateVisibleCount = () => {
      if (window.innerWidth < 760) {
        setVisibleCount(1);
      } else if (window.innerWidth < 1180) {
        setVisibleCount(2);
      } else {
        setVisibleCount(3);
      }
    };

    updateVisibleCount();
    window.addEventListener("resize", updateVisibleCount);
    return () => window.removeEventListener("resize", updateVisibleCount);
  }, []);

  const maxIndex = Math.max(0, supportingReviewCards.length - visibleCount);
  const activeIndex = Math.min(index, maxIndex);
  const visibleReviews = useMemo(
    () => supportingReviewCards.slice(activeIndex, activeIndex + visibleCount),
    [activeIndex, visibleCount],
  );

  const goToPrevious = () => setIndex((current) => Math.max(0, Math.min(current, maxIndex) - 1));
  const goToNext = () => setIndex((current) => Math.min(maxIndex, current + 1));

  if (compact) {
    return (
      <div className="min-w-0">
        <div className="flex items-center justify-between gap-4">
          <p className="text-[0.68rem] font-black uppercase tracking-[0.3em] text-[#a06c15]">Student reviews</p>
          {maxIndex > 0 ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={goToPrevious}
                disabled={activeIndex === 0}
                aria-label="Show previous reviews"
                className="flex h-8 w-8 items-center justify-center rounded-full border border-[#e0cfb4] bg-white text-[#123b34] transition hover:border-[#c99422] hover:text-[#a06c15] disabled:cursor-not-allowed disabled:opacity-35"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true"><path d="M15 5 8 12l7 7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
              <button
                type="button"
                onClick={goToNext}
                disabled={activeIndex >= maxIndex}
                aria-label="Show more reviews"
                className="flex h-8 w-8 items-center justify-center rounded-full border border-[#e0cfb4] bg-white text-[#123b34] transition hover:border-[#c99422] hover:text-[#a06c15] disabled:cursor-not-allowed disabled:opacity-35"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true"><path d="m9 5 7 7-7 7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
            </div>
          ) : null}
        </div>
        <div className="mt-3"><CompactFeaturedReviewCard review={featuredReviewCard} /></div>
        <div className={`mt-3 grid gap-3 ${visibleCount === 1 ? "grid-cols-1" : visibleCount === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
          {visibleReviews.map((review) => <CompactReviewCard key={review.name} review={review} />)}
        </div>
        {maxIndex > 0 ? (
          <div className="mt-3 flex justify-center gap-1.5" aria-hidden="true">
            {Array.from({ length: maxIndex + 1 }).map((_, dotIndex) => (
              <span key={dotIndex} className={`h-1.5 rounded-full transition-all ${dotIndex === activeIndex ? "w-6 bg-[#d3a33b]" : "w-1.5 bg-[#d9c7a9]"}`} />
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between gap-4">
        <p className="text-xs font-black uppercase tracking-[0.34em] text-[#a06c15]">Student reviews</p>
        <div className="flex items-center gap-3">
          <div className="hidden items-center gap-2 sm:flex" aria-hidden="true">
            {Array.from({ length: maxIndex + 1 }).map((_, dotIndex) => (
              <span key={dotIndex} className={`h-2.5 rounded-full transition-all ${dotIndex === activeIndex ? "w-8 bg-[#d3a33b]" : "w-2.5 bg-[#d9c7a9]"}`} />
            ))}
          </div>
          <button
            type="button"
            onClick={goToPrevious}
            disabled={activeIndex === 0}
            aria-label="Show previous reviews"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#e0cfb4] bg-white text-[#123b34] transition hover:border-[#c99422] hover:text-[#a06c15] disabled:cursor-not-allowed disabled:opacity-35"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
              <path d="M15 5 8 12l7 7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            type="button"
            onClick={goToNext}
            disabled={activeIndex >= maxIndex}
            aria-label="Show more reviews"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#e0cfb4] bg-white text-[#123b34] transition hover:border-[#c99422] hover:text-[#a06c15] disabled:cursor-not-allowed disabled:opacity-35"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
              <path d="m9 5 7 7-7 7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
      <div className="mt-4">
        <FeaturedReviewCard review={featuredReviewCard} />
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {visibleReviews.map((review) => (
          <ReviewCard key={review.name} review={review} />
        ))}
      </div>
      <div className="mt-4 flex justify-center gap-2 sm:hidden" aria-hidden="true">
        {Array.from({ length: maxIndex + 1 }).map((_, dotIndex) => (
          <span key={dotIndex} className={`h-2.5 rounded-full transition-all ${dotIndex === activeIndex ? "w-8 bg-[#d3a33b]" : "w-2.5 bg-[#d9c7a9]"}`} />
        ))}
      </div>
    </div>
  );
}

export function CoachingProofSection({
  className = "",
  sourcePage = "home",
  showProofCta = false,
  variant = "default",
}: {
  className?: string;
  sourcePage?: string;
  showProofCta?: boolean;
  variant?: "default" | "compact";
}) {
  if (variant === "compact") {
    return (
      <section
        className={`rounded-[20px] border border-[#dec9a5] bg-[linear-gradient(180deg,rgba(255,254,250,0.97),rgba(255,250,242,0.9))] p-4 shadow-[0_18px_52px_rgba(49,39,24,0.06)] ring-1 ring-white/80 sm:p-5 ${className}`}
        aria-labelledby="public-proof-title"
      >
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(26rem,0.72fr)] lg:items-center">
          <div>
            <p className="text-[0.68rem] font-black uppercase tracking-[0.32em] text-[#a06c15]">Public proof</p>
            <h2 id="public-proof-title" className="mt-1.5 font-serif text-[2rem] font-semibold leading-none tracking-tight text-[#123b34] sm:text-[2.5rem]">
              Real singers. Real progress.
            </h2>
            <p className="mt-2 text-sm leading-5 text-[#68726c] sm:text-base">Google reviews, student portraits and public proof from people who trusted the work.</p>
          </div>
          <div className="flex items-center gap-3 border-t border-[#dfceb2] pt-3 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white shadow-inner ring-1 ring-[#e7dccb]">
              <GoogleGIcon className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <p className="font-black text-[#123b34]">Public Google reviews</p>
                <span className="text-lg font-black text-[#123b34]">{GOOGLE_REVIEW_RATING}</span>
                <Stars />
              </div>
              <p className="mt-1 text-xs leading-5 text-[#66706a] sm:text-sm">{GOOGLE_REVIEW_PROOF_LABEL}.</p>
            </div>
          </div>
        </div>

        <div className="mt-4 grid items-stretch gap-4 lg:grid-cols-[minmax(0,0.78fr)_minmax(0,1.22fr)]">
          <CompactProofVideoCard />
          <ReviewCardCarousel compact />
        </div>
      </section>
    );
  }

  return (
    <section
      className={`rounded-[24px] border border-[#e6dac8] bg-[linear-gradient(180deg,rgba(255,253,248,0.92),rgba(255,250,242,0.78))] p-4 shadow-[0_18px_54px_rgba(49,39,24,0.055)] ring-1 ring-white/80 sm:p-5 ${className}`}
      aria-labelledby="public-proof-title"
    >
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.34em] text-[#a06c15]">Public proof</p>
          <h2 id="public-proof-title" className="mt-2 font-serif text-[2rem] font-semibold leading-none tracking-tight text-[#123b34] sm:text-[2.55rem]">
            Real singers. Real progress.
          </h2>
          <p className="mt-3 max-w-3xl text-base leading-6 text-[#68726c]">
            Google reviews, student portraits and public proof from people who trusted the work.
          </p>
        </div>
        <div className="rounded-[16px] border border-[#e0cfb4] bg-white/88 p-4 shadow-[0_12px_32px_rgba(49,39,24,0.055)]">
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#fffaf2] shadow-inner ring-1 ring-[#e7dccb]">
              <GoogleGIcon className="h-6 w-6" />
            </span>
            <div>
              <p className="text-lg font-black leading-tight text-[#123b34]">Public Google reviews</p>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xl font-black text-[#123b34]">{GOOGLE_REVIEW_RATING}</span>
                <Stars />
              </div>
              <p className="mt-2 text-sm leading-5 text-[#66706a]">
                {GOOGLE_REVIEW_PROOF_LABEL}.
              </p>
            </div>
          </div>
          {showProofCta ? (
            <TrackedLink
              href="/local-lessons"
              eventName="proof_section_cta_click"
              eventProperties={{ source_page: sourcePage }}
              className="mt-4 inline-flex min-h-10 items-center rounded-full border border-[#e0cfb4] bg-white px-4 text-sm font-bold text-[#123b34] transition hover:border-[#c99422] hover:text-[#a06c15]"
            >
              See local lesson options
            </TrackedLink>
          ) : null}
        </div>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[17rem_minmax(0,1fr)] xl:grid-cols-[18rem_minmax(0,1fr)]">
        <ProofVideoCard />
        <ReviewCardCarousel />
      </div>
    </section>
  );
}
